import { useRef, useEffect } from 'react';
import { motion, useInView } from 'framer-motion';
import gsap from 'gsap';
import Lenis from 'lenis';
import {
  Check, Heart, ChevronDown, Bell, UtensilsCrossed,
  MessageSquare, ArrowRight, AlertTriangle, Mail, Plus, Eye,
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { Navigation } from '../../components/landing/NavigationSection';
import { Footer } from '../../components/landing/FooterSection';
import { displayFont, fadeInUp, staggerContainer } from '../../components/landing/shared';
import { Badge } from '../../components/feedback/Badge';
import { TAG_BADGE_CLASS, getTagBg } from '../../utils/tagStyles';

// ─── Luxury theme tokens — matches real LuxuryHeroSection / LuxuryRsvpSection ──

const LX = {
  bg:     '#1a1624',
  gold:   '#b8976d',
  text:   '#f5f0e8',
  muted:  'rgba(245,240,232,0.5)',
  border: 'rgba(184,151,109,0.28)',
  line:   'linear-gradient(90deg, transparent, #b8976d, transparent)',
  font:   "'Instrument Serif', Georgia, serif",
  body:   "'Instrument Sans', system-ui, sans-serif",
} as const;

// Matches real LuxuryRsvpSection squareBtn exactly — borderRadius: 0, uppercase, letter-spaced
function lxSquareBtn(active = false): React.CSSProperties {
  return {
    display: 'block',
    width: '100%',
    padding: '0.75rem 1rem',
    borderRadius: 0,
    border: `1px solid ${active ? LX.text : LX.border}`,
    fontSize: '0.6rem',
    letterSpacing: '0.14em',
    textTransform: 'uppercase' as const,
    color: active ? LX.bg : LX.text,
    backgroundColor: active ? LX.text : 'transparent',
    fontFamily: LX.body,
    cursor: 'pointer',
  };
}

// ─── Data ─────────────────────────────────────────────────────────────────────

const GUESTS = [
  { name: 'Sarah Johnson',        initials: 'SJ', status: 'yes', detail: 'Vegetarian · +1 James Wilson', tags: [{ name: 'Day', color: 'sky' }, { name: 'Top Table', color: 'primary' }], time: '2m ago' },
  { name: 'Tom & Claire Bennett', initials: 'TB', status: 'yes', detail: 'No dietary requirements',      tags: [{ name: 'Day', color: 'sky' }],                                         time: '14m ago' },
  { name: 'Rachel Moore',         initials: 'RM', status: 'yes', detail: 'Gluten free · Chicken',        tags: [{ name: 'Evening', color: 'pink' }],                                    time: '1h ago' },
  { name: 'David Park',           initials: 'DP', status: 'no',  detail: 'Regretfully declines',         tags: [],                                                                      time: '2h ago' },
  { name: 'Mia Clarke',           initials: 'MC', status: 'yes', detail: 'Vegan · Mushroom risotto',     tags: [{ name: 'Day', color: 'sky' }],                                         time: '3h ago' },
];

const DIETARY = [
  { label: 'Vegetarian',  bg: 'bg-emerald-500/10', text: 'text-emerald-700 dark:text-emerald-400' },
  { label: 'Vegan',       bg: 'bg-emerald-500/10', text: 'text-emerald-700 dark:text-emerald-400' },
  { label: 'Gluten free', bg: 'bg-amber-500/10',   text: 'text-warning' },
  { label: 'Nut allergy', bg: 'bg-error/10',       text: 'text-error' },
  { label: 'Dairy free',  bg: 'bg-sky-500/10',     text: 'text-sky-600 dark:text-sky-400' },
  { label: 'Pescatarian', bg: 'bg-sky-500/10',     text: 'text-sky-600 dark:text-sky-400' },
  { label: 'Halal',       bg: 'bg-purple-500/10',  text: 'text-purple-600 dark:text-purple-400' },
  { label: 'Kosher',      bg: 'bg-purple-500/10',  text: 'text-purple-600 dark:text-purple-400' },
];

function RsvpBadge({ status }: { status: string }) {
  return (
    <Badge variant={status === 'yes' ? 'success' : status === 'no' ? 'error' : 'default'} size="sm">
      {status === 'yes' ? 'Attending' : status === 'no' ? 'Declined' : 'Pending'}
    </Badge>
  );
}

// ─── Lenis smooth scroll ───────────────────────────────────────────────────────

function useLenis() {
  useEffect(() => {
    const lenis = new Lenis({ lerp: 0.08, smoothWheel: true });
    import('gsap/ScrollTrigger').then(({ ScrollTrigger }) => {
      gsap.registerPlugin(ScrollTrigger);
      lenis.on('scroll', ScrollTrigger.update);
    });
    gsap.ticker.add((time) => lenis.raf(time * 1000));
    gsap.ticker.lagSmoothing(0);
    return () => { lenis.destroy(); };
  }, []);
}

// ─── Phone frame ──────────────────────────────────────────────────────────────
// 290px wide, 600px tall — slightly larger for more visual impact

const PHONE_FRAME_W = 290;
const PHONE_FRAME_H = 600;
const PHONE_BORDER  = 8;
const PHONE_STATUS  = 30;
// Content area height = frame - 2*border - status bar
const PHONE_CONTENT_H = PHONE_FRAME_H - PHONE_BORDER * 2 - PHONE_STATUS;   // 554

function PhoneMockup({ children }: { children: React.ReactNode }) {
  return (
    <div className="relative mx-auto" style={{ width: PHONE_FRAME_W, flexShrink: 0 }}>
      <div
        className="relative rounded-[2.5rem] overflow-hidden"
        style={{
          width: PHONE_FRAME_W,
          height: PHONE_FRAME_H,
          border: `${PHONE_BORDER}px solid rgba(255,255,255,0.08)`,
          boxShadow: '0 50px 120px -20px rgba(0,0,0,0.55), 0 0 0 1px rgba(255,255,255,0.04)',
        }}>
        {/* Notch */}
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-20 h-5 bg-white/[0.05] rounded-b-[14px] z-20" />
        {/* Status bar */}
        <div className="absolute top-0 left-0 right-0 flex items-center justify-between px-5 z-10"
          style={{ height: PHONE_STATUS, color: 'rgba(255,255,255,0.35)', fontSize: 9 }}>
          <span className="font-bold">9:41</span>
          <span>●●●</span>
        </div>
        {/* Content area — overflow:hidden clips the vertical track */}
        <div className="absolute left-0 right-0 bottom-0 overflow-hidden" style={{ top: PHONE_STATUS }}>
          {children}
        </div>
      </div>
      <div className="absolute bottom-3 left-1/2 -translate-x-1/2 w-14 h-[3px] rounded-full bg-white/15" />
    </div>
  );
}

// ─── Browser frame ─────────────────────────────────────────────────────────────

const BROWSER_CONTENT_H = 380;

function BrowserMockup({ children }: { children: React.ReactNode }) {
  return (
    <div className="w-full rounded-xl overflow-hidden border border-border"
      style={{ boxShadow: '0 32px 80px -16px rgba(0,0,0,0.15)' }}>
      <div className="flex items-center gap-1.5 px-3.5 py-2.5 bg-background-elevated border-b border-border flex-shrink-0">
        <div className="w-2.5 h-2.5 rounded-full bg-error/50" />
        <div className="w-2.5 h-2.5 rounded-full bg-warning/50" />
        <div className="w-2.5 h-2.5 rounded-full bg-success/50" />
        <div className="flex-1 mx-3 h-5 rounded-md bg-background border border-border flex items-center px-2.5 gap-1.5">
          <div className="w-2 h-2 rounded-full bg-success/60" />
          <span className="text-[9px] text-text-muted">wedsy.co/app/guests</span>
        </div>
      </div>
      {/* overflow-hidden clips the vertical track */}
      <div className="overflow-hidden" style={{ height: BROWSER_CONTENT_H }}>
        {children}
      </div>
    </div>
  );
}


// ─── Luxury invite phone screens ───────────────────────────────────────────────
// Exact styling from real LuxuryHeroSection + LuxuryRsvpSection

/** Screen 1: Luxury invite hero — dark bg, gold lines, names */
function LxInviteScreen() {
  return (
    <div
      className="flex flex-col items-center justify-center text-center px-6 py-8"
      style={{ height: PHONE_CONTENT_H, backgroundColor: LX.bg }}>

      {/* Pre-title */}
      <p style={{ fontFamily: LX.body, fontSize: '0.5rem', letterSpacing: '0.3em', textTransform: 'uppercase', color: LX.gold, opacity: 0.85, marginBottom: '1.25rem', fontWeight: 300 }}>
        Together with their families
      </p>

      {/* Short gold line (matches GoldLine component) */}
      <div style={{ height: 1, width: 60, background: LX.line, marginBottom: '1.5rem' }} />

      {/* Names — Instrument Serif, weight 300, matching nameStyle */}
      <h2 style={{ fontFamily: LX.font, fontWeight: 300, fontSize: '2rem', lineHeight: 1.1, color: LX.text, marginBottom: '0.15rem' }}>
        Emma
      </h2>
      <p style={{ fontFamily: LX.font, fontStyle: 'italic', fontWeight: 300, fontSize: '1.1rem', lineHeight: 1.6, color: LX.gold, margin: '0.1rem 0' }}>
        &amp;
      </p>
      <h2 style={{ fontFamily: LX.font, fontWeight: 300, fontSize: '2rem', lineHeight: 1.1, color: LX.text, marginBottom: '1.5rem' }}>
        James
      </h2>

      {/* Long gold line */}
      <div style={{ height: 1, width: 100, background: LX.line, marginBottom: '1.5rem' }} />

      {/* Date + venue */}
      <p style={{ fontFamily: LX.body, fontWeight: 300, fontSize: '0.5rem', letterSpacing: '0.2em', textTransform: 'uppercase', color: LX.muted, marginBottom: '0.3rem' }}>
        Saturday 14th June 2026
      </p>
      <p style={{ fontFamily: LX.body, fontWeight: 300, fontSize: '0.5rem', letterSpacing: '0.15em', textTransform: 'uppercase', color: LX.muted }}>
        The Grand Manor, Surrey
      </p>

      {/* Scroll cue */}
      <div style={{ marginTop: '2rem', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.4rem' }}>
        <p style={{ fontFamily: LX.body, fontSize: '0.42rem', letterSpacing: '0.3em', textTransform: 'uppercase', color: 'rgba(245,240,232,0.25)' }}>
          Scroll to explore
        </p>
        <div style={{ width: 1, height: 20, background: `linear-gradient(to bottom, ${LX.gold}, transparent)` }} />
      </div>
    </div>
  );
}

/** Screen 2: RSVP form — square buttons, uppercase, exact LuxuryRsvpSection style */
function LxRsvpScreen() {
  return (
    <div style={{ height: PHONE_CONTENT_H, backgroundColor: LX.bg, padding: '1.5rem 1.25rem', overflowY: 'hidden', display: 'flex', flexDirection: 'column' }}>

      {/* RSVP label + icon */}
      <div style={{ textAlign: 'center', marginBottom: '0.875rem' }}>
        <p style={{ fontFamily: LX.body, fontSize: '0.5rem', letterSpacing: '0.35em', textTransform: 'uppercase', color: LX.gold }}>RSVP</p>
      </div>

      {/* Heading — two lines, second in italic gold (matches real LuxuryRsvpSection) */}
      <div style={{ textAlign: 'center', marginBottom: '0.75rem' }}>
        <span style={{ fontFamily: LX.font, fontWeight: 300, fontSize: '1.5rem', color: LX.text, display: 'block', lineHeight: 1.2 }}>
          We&apos;d love to
        </span>
        <span style={{ fontFamily: LX.font, fontStyle: 'italic', fontWeight: 300, fontSize: '1.5rem', color: LX.gold, display: 'block', lineHeight: 1.2 }}>
          see you there
        </span>
      </div>

      <div style={{ height: 1, width: 60, background: LX.line, margin: '0 auto 1.25rem' }} />

      {/* Guest name */}
      <p style={{ fontFamily: LX.font, fontStyle: 'italic', fontWeight: 300, fontSize: '0.85rem', color: LX.muted, textAlign: 'center', marginBottom: '1rem' }}>
        Dear Sarah,
      </p>

      {/* Square RSVP buttons — exactly matching squareBtn in LuxuryRsvpSection */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
        <button style={lxSquareBtn(true)}>Joyfully Accept</button>
        <button style={lxSquareBtn(false)}>Regretfully Decline</button>
        <button style={lxSquareBtn(false)}>Not Sure Yet</button>
      </div>
    </div>
  );
}

/** Screen 3: Guest details — transparent inputs, square checkboxes */
function LxDetailsScreen() {
  const fieldStyle: React.CSSProperties = {
    backgroundColor: 'transparent',
    border: `1px solid ${LX.border}`,
    borderRadius: 0,
    color: LX.text,
    fontFamily: LX.body,
    fontSize: '0.7rem',
    padding: '0.5rem 0.625rem',
    width: '100%',
  };
  const labelStyle: React.CSSProperties = {
    fontFamily: LX.body,
    fontSize: '0.48rem',
    letterSpacing: '0.15em',
    textTransform: 'uppercase' as const,
    color: LX.gold,
    display: 'block',
    marginBottom: '0.35rem',
  };

  return (
    <div style={{ height: PHONE_CONTENT_H, backgroundColor: LX.bg, padding: '1.5rem 1.25rem', overflowY: 'hidden' }}>
      <p style={{ fontFamily: LX.body, fontSize: '0.5rem', letterSpacing: '0.3em', textTransform: 'uppercase', color: LX.gold, textAlign: 'center', marginBottom: '1rem' }}>
        Your details
      </p>
      <div style={{ height: 1, width: 60, background: LX.line, margin: '0 auto 1.25rem' }} />

      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.875rem' }}>
        <div>
          <label style={labelStyle}>Dietary requirements</label>
          <div style={{ ...fieldStyle, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <span>Vegetarian</span>
            <ChevronDown size={10} style={{ color: LX.muted }} />
          </div>
        </div>

        <div>
          <label style={labelStyle}>Will you bring a plus one?</label>
          <div style={{ display: 'flex', gap: '0.4rem' }}>
            {['Yes please', 'Just me'].map((opt, i) => (
              <button key={opt} style={{ ...lxSquareBtn(i === 0), flex: 1, padding: '0.5rem' }}>{opt}</button>
            ))}
          </div>
        </div>

        <div>
          <label style={labelStyle}>Plus one name</label>
          <div style={fieldStyle}>James Wilson</div>
        </div>
      </div>

      <div style={{ marginTop: '1rem' }}>
        <button style={{ ...lxSquareBtn(false), borderColor: LX.gold, color: LX.text }}>
          Send response
        </button>
      </div>
    </div>
  );
}

/** Screen 4: Confirmation — SQUARE border box (not circle), gold heart */
function LxConfirmScreen() {
  return (
    <div style={{ height: PHONE_CONTENT_H, backgroundColor: LX.bg, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '1.5rem 1.25rem', textAlign: 'center' }}>

      {/* Square border box — exactly as in real LuxuryRsvpSection isSubmitted state */}
      <div style={{ width: 56, height: 56, border: `1px solid ${LX.gold}`, borderRadius: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '1.25rem' }}>
        <Heart size={22} style={{ color: LX.gold }} fill={LX.gold} />
      </div>

      <h3 style={{ fontFamily: LX.font, fontWeight: 300, fontSize: '1.4rem', color: LX.text, lineHeight: 1.25, marginBottom: '0.25rem' }}>
        We can&apos;t wait<br />to see you
      </h3>

      <div style={{ height: 1, width: 60, background: LX.line, margin: '0.875rem auto' }} />

      <p style={{ fontFamily: LX.body, fontWeight: 300, fontSize: '0.6rem', letterSpacing: '0.05em', color: LX.muted, marginBottom: '1.25rem' }}>
        Your response has been recorded
      </p>

      {/* Response summary — matching real summary table, borderRadius 0 */}
      <div style={{ border: `1px solid ${LX.border}`, width: '100%', borderRadius: 0 }}>
        {[['Status', 'Attending'], ['Plus one', 'James Wilson'], ['Dietary', 'Vegetarian'], ['Meal', 'Roasted chicken']].map(([label, val]) => (
          <div key={label} style={{ display: 'flex', justifyContent: 'space-between', padding: '0.4rem 0.625rem', borderBottom: `1px solid ${LX.border}` }}>
            <span style={{ fontFamily: LX.body, fontSize: '0.52rem', letterSpacing: '0.08em', textTransform: 'uppercase', color: LX.muted }}>{label}</span>
            <span style={{ fontFamily: LX.body, fontSize: '0.55rem', color: LX.text }}>{val}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── Dashboard browser screens ────────────────────────────────────────────────
// Real guest list UI — Badge, TAG_BADGE_CLASS + getTagBg(), table rows

/** Screen 1: Stats + live feed (matching real RsvpStatsSection + activity view) */
function DashStatsScreen() {
  return (
    <div className="px-5 py-4 h-full bg-background overflow-hidden">
      {/* Stats row — exact stat card style from real app */}
      <div className="grid grid-cols-3 gap-2.5 mb-4">
        {[
          { n: '24', label: 'Attending', cls: 'text-success' },
          { n: '3',  label: 'Declined',  cls: 'text-error' },
          { n: '8',  label: 'Pending',   cls: 'text-text-muted' },
        ].map(s => (
          <div key={s.label} className="rounded-xl bg-background-card border border-border px-3 py-2.5 text-center">
            <div className={`text-xl font-bold leading-none ${s.cls}`} style={displayFont}>{s.n}</div>
            <div className="text-[9px] uppercase tracking-wider text-text-muted mt-1">{s.label}</div>
          </div>
        ))}
      </div>

      {/* Live feed */}
      <div className="flex items-center gap-1.5 mb-2">
        <span className="w-1.5 h-1.5 rounded-full bg-success animate-pulse" />
        <span className="text-[9px] uppercase tracking-wider text-text-muted font-medium">Live responses</span>
      </div>

      <div className="space-y-1.5">
        {GUESTS.slice(0, 4).map(g => (
          <div key={g.name} className="flex items-center gap-2.5 px-3 py-2 rounded-xl bg-background-card border border-border">
            <div className="w-7 h-7 rounded-full bg-primary/10 flex items-center justify-center text-[10px] font-bold text-primary flex-shrink-0">{g.initials}</div>
            <div className="flex-1 min-w-0">
              <p className="text-xs font-semibold text-text-primary truncate">{g.name}</p>
              <p className="text-[9px] text-text-muted truncate">{g.detail}</p>
            </div>
            <RsvpBadge status={g.status} />
            <span className="text-[9px] text-text-muted flex-shrink-0">{g.time}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

/** Screen 2: Full guest table — real table row pattern with Badge + tags */
function DashGuestListScreen() {
  return (
    <div className="h-full bg-background overflow-hidden">
      {/* Table header */}
      <div className="flex items-center gap-3 px-4 py-2 bg-background-elevated border-b border-border">
        <div className="w-7 flex-shrink-0" />
        <span className="flex-1 text-[9px] uppercase tracking-wider text-text-muted">Guest</span>
        <span className="w-20 text-[9px] uppercase tracking-wider text-text-muted">RSVP</span>
        <span className="flex-1 text-[9px] uppercase tracking-wider text-text-muted">Tags</span>
        <span className="w-6" />
      </div>

      {/* Rows — exact structure from GuestRow in GuestListPage */}
      <div className="divide-y divide-border/50">
        {GUESTS.map(g => (
          <div key={g.name} className="flex items-center gap-3 px-4 py-2.5 hover:bg-background-elevated/50 transition-colors">
            {/* Avatar */}
            <div className="w-7 h-7 rounded-full bg-primary/10 flex items-center justify-center text-[10px] font-bold text-primary flex-shrink-0">{g.initials}</div>
            {/* Name + detail */}
            <div className="flex-1 min-w-0">
              <p className="text-xs font-semibold text-text-primary truncate">{g.name}</p>
              <p className="text-[9px] text-text-muted truncate">{g.detail}</p>
            </div>
            {/* RSVP badge — same as real GuestRow */}
            <div className="w-20 flex-shrink-0">
              <RsvpBadge status={g.status} />
            </div>
            {/* Tags — TAG_BADGE_CLASS + getTagBg (real pattern) */}
            <div className="flex-1 flex gap-1 flex-wrap">
              {g.tags.map(tag => (
                <span key={tag.name} className={`${TAG_BADGE_CLASS} ${getTagBg(tag.color)}`}>{tag.name}</span>
              ))}
            </div>
            {/* View count */}
            <div className="w-6 flex items-center gap-0.5 text-text-muted flex-shrink-0">
              <Eye size={10} />
              <span className="text-[9px]">{Math.floor(Math.random() * 5) + 1}</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/** Screen 3: Expanded guest detail (Sarah Johnson) — real app pattern */
function DashGuestDetailScreen() {
  return (
    <div className="h-full bg-background overflow-hidden">
      {/* Guest header — matches real GuestRow expanded header */}
      <div className="flex items-center gap-3 px-4 py-3 bg-primary/[0.03] border-b border-border">
        <div className="w-9 h-9 rounded-full bg-primary/10 flex items-center justify-center text-sm font-bold text-primary flex-shrink-0">SJ</div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-text-primary">Sarah Johnson</p>
          <p className="text-[10px] text-text-muted">sarah@email.com</p>
        </div>
        <Badge variant="success" size="sm" icon={<Check size={9} />}>Attending</Badge>
      </div>

      <div className="px-4 py-3 space-y-3">
        {/* Tags */}
        <div className="flex gap-1.5 flex-wrap">
          {[{ name: 'Day', color: 'sky' }, { name: 'Top Table', color: 'primary' }].map(tag => (
            <span key={tag.name} className={`${TAG_BADGE_CLASS} ${getTagBg(tag.color)}`}>{tag.name}</span>
          ))}
        </div>

        {/* Detail rows — exact structure from real app guest detail */}
        <div className="rounded-xl border border-border bg-background-card divide-y divide-border">
          {[
            ['Plus one',    'James Wilson'],
            ['Dietary',     'Vegetarian'],
            ['Meal choice', 'Roasted chicken'],
            ['Song request','Dancing Queen — ABBA'],
          ].map(([label, val]) => (
            <div key={label} className="flex items-center justify-between px-3.5 py-2">
              <span className="text-[10px] text-text-muted">{label}</span>
              <span className="text-[10px] text-text-primary font-semibold">{val}</span>
            </div>
          ))}
        </div>

        {/* Notification badge */}
        <div className="flex items-center gap-2 p-2.5 rounded-xl bg-success/5 border border-success/20">
          <Check size={11} className="text-success flex-shrink-0" />
          <span className="text-[10px] text-text-secondary">RSVP received · 2 minutes ago</span>
        </div>
      </div>
    </div>
  );
}

// ─── Form Chapter ──────────────────────────────────────────────────────────────
// Dark section, large phone centered, screens scroll VERTICALLY (not horizontal)
// Vertical feel matches real phone navigation — much more cinematic than a slider

const FORM_COPIES = [
  { badge: 'No app needed',  heading: 'One link.\nThat\'s it.',          body: 'Guests tap your link, see a beautiful invite, and RSVP instantly. No app to download, no account required.' },
  { badge: 'RSVP form',      heading: 'Accept or decline\nin seconds.',  body: 'Three clear options. As elegant as the invite itself. One tap to confirm — and you know instantly.' },
  { badge: 'Guest details',  heading: 'Everything you\nneed, captured.', body: 'Dietary requirements, meal choices, plus ones — all collected as part of the RSVP. No chasing.' },
  { badge: 'Confirmed',      heading: 'Done.\nBeautifully.',             body: 'A stunning confirmation the moment they submit. Guests can return and update any time.' },
];

function FormChapter() {
  const wrapperRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const wrapper = wrapperRef.current;
    if (!wrapper) return;
    let ctx: gsap.Context;
    import('gsap/ScrollTrigger').then(({ ScrollTrigger }) => {
      gsap.registerPlugin(ScrollTrigger);
      ctx = gsap.context(() => {
        const track  = wrapper.querySelector<HTMLElement>('.form-track');
        const copies = Array.from(wrapper.querySelectorAll<HTMLElement>('.form-copy'));
        const dots   = Array.from(wrapper.querySelectorAll<HTMLElement>('.form-dot'));
        if (!track) return;

        const NUM = FORM_COPIES.length;

        copies.forEach((c, i) => gsap.set(c, { opacity: i === 0 ? 1 : 0, y: i === 0 ? 0 : 30 }));
        dots.forEach((d, i) => gsap.set(d, { width: i === 0 ? 20 : 6, opacity: i === 0 ? 1 : 0.4 }));

        ScrollTrigger.create({
          trigger: wrapper,
          start: 'top top',
          end: 'bottom bottom',
          scrub: 1.2,
          onUpdate(self) {
            const step = self.progress * (NUM - 1);
            gsap.set(track, { y: -PHONE_CONTENT_H * step });
            copies.forEach((copy, i) => {
              const dist = step - i;
              gsap.set(copy, { opacity: Math.max(0, 1 - Math.abs(dist) * 2), y: dist * -24 });
            });
            dots.forEach((dot, i) => {
              const active = Math.abs(step - i) < 0.55;
              gsap.set(dot, { width: active ? 20 : 6, opacity: active ? 1 : 0.35 });
            });
          },
        });
      }, wrapper);
    });
    return () => { ctx?.revert(); };
  }, []);

  return (
    <div ref={wrapperRef} style={{ height: '400vh' }} className="relative">
      {/* Dark section — luxury invite pops against dark bg */}
      <div className="sticky top-0 h-screen flex flex-col justify-center" style={{ backgroundColor: '#0f0d18' }}>

        {/* Section label */}
        <div className="absolute top-8 left-0 right-0 flex justify-center z-10">
          <span className="inline-block px-4 py-2 rounded-full text-sm font-medium"
            style={{ backgroundColor: 'rgba(184,151,109,0.12)', color: LX.gold }}>
            Guest experience
          </span>
        </div>

        <div className="w-full max-w-6xl mx-auto px-6 lg:px-16 grid lg:grid-cols-[1fr_auto] gap-12 lg:gap-20 items-center">

          {/* Copy panels — absolutely stacked, GSAP drives opacity + y */}
          <div className="relative order-2 lg:order-1" style={{ height: 280 }}>
            {FORM_COPIES.map((c, i) => (
              <div key={i} className="form-copy absolute inset-0 flex flex-col justify-center">
                <span className="inline-block self-start px-4 py-2 rounded-full text-sm font-medium mb-5"
                  style={{ backgroundColor: 'rgba(184,151,109,0.12)', color: LX.gold }}>
                  {c.badge}
                </span>
                <h2 className="text-4xl sm:text-5xl lg:text-[3.2rem] leading-[1.08] whitespace-pre-line mb-4"
                  style={{ ...displayFont, color: LX.text }}>
                  {c.heading}
                </h2>
                <p className="text-base leading-relaxed max-w-sm" style={{ color: LX.muted }}>
                  {c.body}
                </p>
              </div>
            ))}
          </div>

          {/* Phone — vertical track scrolls screens UP */}
          <div className="order-1 lg:order-2 flex flex-col items-center">
            <PhoneMockup>
              {/* Vertical flex-col track, GSAP translates y */}
              <div
                className="form-track flex flex-col"
                style={{ height: PHONE_CONTENT_H * FORM_COPIES.length }}>
                {[<LxInviteScreen />, <LxRsvpScreen />, <LxDetailsScreen />, <LxConfirmScreen />].map((screen, i) => (
                  <div key={i} style={{ height: PHONE_CONTENT_H, flexShrink: 0 }}>
                    {screen}
                  </div>
                ))}
              </div>
            </PhoneMockup>

            {/* Step dots */}
            <div className="flex items-center justify-center gap-2 mt-6">
              {FORM_COPIES.map((_, i) => (
                <div key={i} className="form-dot rounded-full"
                  style={{ height: 6, width: 6, backgroundColor: LX.gold, opacity: 0.35 }} />
              ))}
            </div>
          </div>

        </div>
      </div>
    </div>
  );
}

// ─── Dashboard Chapter ─────────────────────────────────────────────────────────
// Light section, browser wide and centered, real guest table UI scrolls vertically

const DASH_COPIES = [
  { badge: 'Live dashboard', heading: 'Watch responses\nroll in.',   body: 'Every RSVP lands the moment it\'s submitted. See who\'s in, who\'s out, and who needs a nudge — in real time.' },
  { badge: 'Guest list',     heading: 'Every guest,\nat a glance.',  body: 'Name, dietary, RSVP status, tags — all in one view. Filter, sort, and search across your entire guest list.' },
  { badge: 'Guest detail',   heading: 'Every answer,\nright there.', body: 'Click any guest and see everything — dietary requirements, meal choice, plus ones, custom question answers.' },
];

function DashboardChapter() {
  const wrapperRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const wrapper = wrapperRef.current;
    if (!wrapper) return;
    let ctx: gsap.Context;
    import('gsap/ScrollTrigger').then(({ ScrollTrigger }) => {
      gsap.registerPlugin(ScrollTrigger);
      ctx = gsap.context(() => {
        const track  = wrapper.querySelector<HTMLElement>('.dash-track');
        const copies = Array.from(wrapper.querySelectorAll<HTMLElement>('.dash-copy'));
        const dots   = Array.from(wrapper.querySelectorAll<HTMLElement>('.dash-dot'));
        if (!track) return;

        const NUM = DASH_COPIES.length;

        copies.forEach((c, i) => gsap.set(c, { opacity: i === 0 ? 1 : 0, y: i === 0 ? 0 : 30 }));
        dots.forEach((d, i) => gsap.set(d, { width: i === 0 ? 20 : 6, opacity: i === 0 ? 1 : 0.3 }));

        ScrollTrigger.create({
          trigger: wrapper,
          start: 'top top',
          end: 'bottom bottom',
          scrub: 1.2,
          onUpdate(self) {
            const step = self.progress * (NUM - 1);
            gsap.set(track, { y: -BROWSER_CONTENT_H * step });
            copies.forEach((copy, i) => {
              const dist = step - i;
              gsap.set(copy, { opacity: Math.max(0, 1 - Math.abs(dist) * 2), y: dist * -24 });
            });
            dots.forEach((dot, i) => {
              const active = Math.abs(step - i) < 0.55;
              gsap.set(dot, { width: active ? 20 : 6, opacity: active ? 1 : 0.3 });
            });
          },
        });
      }, wrapper);
    });
    return () => { ctx?.revert(); };
  }, []);

  return (
    <div ref={wrapperRef} style={{ height: '320vh' }} className="relative">
      <div className="sticky top-0 h-screen bg-background-card border-t border-border flex flex-col justify-center">

        <div className="absolute top-8 left-0 right-0 flex justify-center z-10">
          <span className="inline-block px-4 py-2 bg-primary/10 rounded-full text-sm font-medium text-primary">
            Your dashboard
          </span>
        </div>

        <div className="w-full max-w-6xl mx-auto px-6 lg:px-16">

          {/* Copy panels above browser */}
          <div className="relative mb-8" style={{ height: 160 }}>
            {DASH_COPIES.map((c, i) => (
              <div key={i} className="dash-copy absolute inset-0 flex flex-col justify-center">
                <div className="text-center">
                  <span className="inline-block px-4 py-2 bg-primary/10 rounded-full text-sm font-medium text-primary mb-4">
                    {c.badge}
                  </span>
                  <h2 className="text-3xl sm:text-4xl lg:text-5xl leading-[1.08] whitespace-pre-line text-text-primary mb-3" style={displayFont}>
                    {c.heading}
                  </h2>
                  <p className="text-base text-text-secondary max-w-lg mx-auto">
                    {c.body}
                  </p>
                </div>
              </div>
            ))}
          </div>

          {/* Browser mockup — vertical track inside */}
          <BrowserMockup>
            <div
              className="dash-track"
              style={{ height: BROWSER_CONTENT_H * DASH_COPIES.length }}>
              {[<DashStatsScreen />, <DashGuestListScreen />, <DashGuestDetailScreen />].map((screen, i) => (
                <div key={i} style={{ height: BROWSER_CONTENT_H, flexShrink: 0 }}>
                  {screen}
                </div>
              ))}
            </div>
          </BrowserMockup>

          {/* Step dots */}
          <div className="flex items-center justify-center gap-2 mt-5">
            {DASH_COPIES.map((_, i) => (
              <div key={i} className="dash-dot rounded-full"
                style={{ height: 6, width: 6, backgroundColor: 'currentColor', opacity: 0.3 }} />
            ))}
          </div>

        </div>
      </div>
    </div>
  );
}

// ─── Dietary ───────────────────────────────────────────────────────────────────

const chipOffsets = [
  { x: -18, y: 10, r: -7 }, { x: 14, y: -12, r: 5 }, { x: -10, y: 14, r: -3 }, { x: 22, y: -8, r: 8 },
  { x: -16, y: 12, r: -5 }, { x: 11, y: -14, r: 4 }, { x: -8, y: 10, r: -2 },  { x: 13, y: -7, r: 6 },
];

function DietarySection() {
  const ref = useRef<HTMLDivElement>(null);
  const inView = useInView(ref, { once: true, margin: '-80px' });

  return (
    <section ref={ref} className="py-28 px-6 bg-background border-y border-border">
      <div className="max-w-5xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
        <motion.div initial={false} animate={inView ? { opacity: 1, x: 0 } : {}} transition={{ duration: 0.6 }}>
          <span className="inline-flex items-center gap-2 px-4 py-2 bg-primary/10 rounded-full text-sm font-medium text-primary mb-5">
            <UtensilsCrossed size={13} /> Dietary requirements
          </span>
          <h2 className="text-4xl sm:text-5xl text-text-primary mb-4 leading-tight" style={displayFont}>
            Every need,<br /><span className="text-primary">noted.</span>
          </h2>
          <p className="text-text-secondary text-base leading-relaxed mb-6">
            Guests pick their dietary requirements as part of the RSVP. No chasing, no separate form.
          </p>
          <div className="flex items-start gap-2.5 text-sm text-text-secondary p-3.5 rounded-xl bg-warning/8 border border-warning/20">
            <AlertTriangle size={13} className="text-warning flex-shrink-0 mt-0.5" />
            <span>Allergy warnings are highlighted automatically so nothing gets missed.</span>
          </div>
        </motion.div>
        <div className="flex flex-wrap gap-2.5">
          {DIETARY.map((item, i) => (
            <motion.div
              key={item.label}
              initial={false}
              animate={inView ? { opacity: 1, scale: 1, x: 0, y: 0, rotate: 0 } : {}}
              transition={{ duration: 0.45, delay: 0.08 + i * 0.07, type: 'spring', stiffness: 200, damping: 18 }}
              className={`inline-flex items-center px-4 py-2 rounded-full text-sm font-semibold ${item.bg} ${item.text}`}>
              {item.label}
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ─── Custom Questions ──────────────────────────────────────────────────────────

function QuestionsSection() {
  const ref = useRef<HTMLDivElement>(null);
  const inView = useInView(ref, { once: true, margin: '-80px' });

  return (
    <section ref={ref} className="py-28 px-6 bg-background-card">
      <div className="max-w-5xl mx-auto">
        <motion.div initial={false} animate={inView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.55 }}
          className="text-center max-w-xl mx-auto mb-14">
          <span className="inline-flex items-center gap-2 px-4 py-2 bg-primary/10 rounded-full text-sm font-medium text-primary mb-5">
            <MessageSquare size={13} /> Custom questions
          </span>
          <h2 className="text-4xl sm:text-5xl text-text-primary mb-4" style={displayFont}>
            Ask exactly<br />what you need.
          </h2>
          <p className="text-text-secondary text-base leading-relaxed">
            Add your own questions — multiple choice for meal selections, free text for everything else.
          </p>
        </motion.div>
        <div className="grid sm:grid-cols-2 gap-5 max-w-xl mx-auto">
          <motion.div
            initial={false}
            animate={inView ? { opacity: 1, y: 0, rotate: -1.5 } : {}}
            transition={{ duration: 0.5, delay: 0.12 }}
            className="rounded-2xl bg-background border border-border shadow-sm p-5 space-y-3">
            <div>
              <p className="text-[9px] font-bold text-text-muted uppercase tracking-wider mb-1">Multiple choice</p>
              <p className="text-sm font-semibold text-text-primary">What would you like for your main?</p>
            </div>
            <div className="space-y-2">
              {['Roasted chicken', 'Pan-seared salmon', 'Mushroom risotto'].map((opt, i) => (
                <div key={opt}
                  className={`flex items-center gap-2.5 px-3 py-2 rounded-xl border text-sm ${i === 0 ? 'border-primary/40 bg-primary/5 text-primary font-semibold' : 'border-border text-text-secondary'}`}>
                  <div className={`w-4 h-4 rounded-full border-2 flex-shrink-0 flex items-center justify-center ${i === 0 ? 'border-primary bg-primary' : 'border-border'}`}>
                    {i === 0 && <Check size={8} className="text-white" />}
                  </div>
                  {opt}
                </div>
              ))}
            </div>
          </motion.div>
          <motion.div
            initial={false}
            animate={inView ? { opacity: 1, y: 0, rotate: 1.5 } : {}}
            transition={{ duration: 0.5, delay: 0.24 }}
            className="rounded-2xl bg-background border border-border shadow-sm p-5 space-y-3">
            <div>
              <p className="text-[9px] font-bold text-text-muted uppercase tracking-wider mb-1">Free text</p>
              <p className="text-sm font-semibold text-text-primary">Any song requests for the evening?</p>
            </div>
            <div className="px-3 py-2.5 rounded-xl border border-border bg-background-card text-sm text-text-secondary min-h-[80px]">
              Mr. Brightside — The Killers
              <span className="border-r-2 border-text-muted ml-px animate-pulse">&nbsp;</span>
            </div>
            <p className="text-xs text-text-muted">Answers appear on your dashboard next to each guest.</p>
          </motion.div>
        </div>
      </div>
    </section>
  );
}

// ─── Reminders ─────────────────────────────────────────────────────────────────

function RemindersSection() {
  const ref = useRef<HTMLDivElement>(null);
  const inView = useInView(ref, { once: true, margin: '-80px' });

  const steps = [
    { icon: <Plus size={13} />,       label: 'Invite published',           sub: 'Link sent to all guests' },
    { icon: <Bell size={13} />,        label: 'Automated reminder sent',    sub: 'To guests who haven\'t replied' },
    { icon: <Check size={13} />,       label: 'RSVP deadline reached',      sub: 'Responses locked, list finalised' },
  ];

  return (
    <section ref={ref} className="py-28 px-6 bg-background">
      <div className="max-w-5xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">

        <motion.div
          variants={staggerContainer}
          initial={false}
          animate={inView ? 'visible' : 'hidden'}
          className="space-y-4 order-2 lg:order-1">
          {steps.map((s, i) => (
            <motion.div
              key={i}
              variants={fadeInUp}
              className="flex items-center gap-4 p-4 rounded-xl bg-background-card border border-border">
              <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center text-primary flex-shrink-0">
                {s.icon}
              </div>
              <div>
                <p className="text-sm font-semibold text-text-primary">{s.label}</p>
                <p className="text-xs text-text-muted">{s.sub}</p>
              </div>
            </motion.div>
          ))}
          <motion.div
            initial={false} animate={inView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.45, delay: 0.5 }}
            className="flex items-center gap-2.5 p-3.5 rounded-xl bg-success/5 border border-success/20">
            <Mail size={13} className="text-success flex-shrink-0" />
            <p className="text-sm text-text-secondary">You get an email the moment someone RSVPs.</p>
          </motion.div>
        </motion.div>

        <motion.div initial={false} animate={inView ? { opacity: 1, x: 0 } : {}} transition={{ duration: 0.6 }}
          className="order-1 lg:order-2">
          <span className="inline-flex items-center gap-2 px-4 py-2 bg-primary/10 rounded-full text-sm font-medium text-primary mb-5">
            <Bell size={13} /> Reminders
          </span>
          <h2 className="text-4xl sm:text-5xl text-text-primary mb-4 leading-tight" style={displayFont}>
            No chasing.<br /><span className="text-primary">Ever.</span>
          </h2>
          <p className="text-text-secondary text-base leading-relaxed">
            Set a reminder and Wedsy follows up with guests who haven't replied yet — automatically. You get notified the moment anyone responds.
          </p>
        </motion.div>

      </div>
    </section>
  );
}

// ─── CTA ───────────────────────────────────────────────────────────────────────

function RsvpCta() {
  const ref = useRef<HTMLDivElement>(null);
  const inView = useInView(ref, { once: true, margin: '-80px' });

  return (
    <section ref={ref} className="py-32 px-6 bg-background-card border-t border-border text-center">
      <div className="max-w-xl mx-auto">
        <motion.h2 initial={false} animate={inView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.6 }}
          className="text-4xl sm:text-5xl lg:text-6xl text-text-primary mb-5" style={displayFont}>
          Ready to take the stress<br />out of RSVPs?
        </motion.h2>
        <motion.p initial={false} animate={inView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.55, delay: 0.1 }}
          className="text-lg text-text-secondary mb-10">
          Create your invite, share the link, and let Wedsy handle the rest.
        </motion.p>
        <motion.div initial={false} animate={inView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.5, delay: 0.18 }}
          className="flex flex-col sm:flex-row items-center justify-center gap-3">
          <Link to="/register" className="btn btn-primary hover:shadow-lg">
            Get started free <ArrowRight size={15} />
          </Link>
          <Link to="/" className="btn btn-secondary">
            See all features
          </Link>
        </motion.div>
      </div>
    </section>
  );
}

// ─── Hero ──────────────────────────────────────────────────────────────────────

function RsvpHero() {
  return (
    <section className="relative min-h-[88vh] flex flex-col items-center justify-center text-center px-6 bg-background border-b border-border">
      <motion.div initial={false} animate="visible" variants={staggerContainer} className="relative z-10 max-w-4xl">
        <motion.span variants={fadeInUp}
          className="inline-block px-4 py-2 bg-primary/10 rounded-full text-sm font-medium text-primary mb-6">
          RSVP
        </motion.span>
        <motion.h1 variants={fadeInUp}
          className="text-5xl sm:text-6xl lg:text-7xl text-text-primary mb-6 leading-[1.05]" style={displayFont}>
          The RSVP experience<br /><span className="text-primary">your guests deserve</span>
        </motion.h1>
        <motion.p variants={fadeInUp}
          className="text-lg sm:text-xl text-text-secondary max-w-2xl mx-auto mb-10">
          No app. No account. One tap — and you know exactly who's coming, what they need, and who to chase.
        </motion.p>
        <motion.div variants={fadeInUp} className="flex flex-col sm:flex-row items-center justify-center gap-3">
          <Link to="/register" className="btn-hero btn-primary hover:shadow-lg">Get started free <ArrowRight size={16} /></Link>
          <Link to="/#features" className="btn-hero btn-secondary">See all features</Link>
        </motion.div>
      </motion.div>
      <motion.div className="absolute bottom-8 flex flex-col items-center gap-1.5"
        initial={false} animate={{ opacity: 1 }} transition={{ delay: 1 }}>
        <span className="text-[9px] tracking-widest uppercase text-text-muted">Scroll</span>
        <motion.div animate={{ y: [0, 5, 0] }} transition={{ repeat: Infinity, duration: 1.6 }}>
          <ChevronDown size={14} className="text-text-muted" />
        </motion.div>
      </motion.div>
    </section>
  );
}

// ─── Page ──────────────────────────────────────────────────────────────────────

export function RsvpFeaturePage() {
  useLenis();
  return (
    <div className="overflow-x-clip">
      <Navigation />
      <RsvpHero />
      <FormChapter />
      <DashboardChapter />
      <DietarySection />
      <QuestionsSection />
      <RemindersSection />
      <RsvpCta />
      <Footer />
    </div>
  );
}
